import React from 'react';

import { CollectionNode } from '@/services/types/collection-types';
import { CompanionMember } from '@/services/types/companions-types';

import { ActionDialog } from './ActionDialog';
import { TransferToCompanionsDialog } from './TransferToCompanionsDialog';
import { UploadDialog } from './UploadDialog';

interface DocumentDialogsProps {
  // Collection data
  collectionTree: CollectionNode | undefined;

  // Upload dialog props
  uploadDialogOpen: boolean;
  uploadDialogTargetId: string | null;
  closeUploadDialog: () => void;
  handleUploadToCollection: (
    files: File[],
    collectionId: string,
    autoSplitLarge: boolean,
    uploadToCompanions: boolean,
    targetCompanionMatterIds?: string[],
  ) => Promise<string[] | void>;
  companions?: CompanionMember[];

  // Bulk move dialog props
  bulkMoveDialogOpen: boolean;
  bulkMoveTargetCollectionId: string;
  closeBulkMoveDialog: () => void;
  executeBulkMove: () => void;
  setBulkMoveTargetCollectionId: (id: string) => void;
  bulkOperationError: string | null;
  isProcessing: boolean;
  selectedFileCount: number;

  // Bulk delete dialog props
  bulkDeleteDialogOpen: boolean;
  closeBulkDeleteDialog: () => void;
  executeBulkDelete: () => void;

  // Bulk regenerate dialog props
  bulkRegenerateDialogOpen: boolean;
  closeBulkRegenerateDialog: () => void;
  executeBulkRegenerate: () => void;

  // Transfer to companions dialog props
  transferToCompanionsDialogOpen: boolean;
  closeTransferToCompanionsDialog: () => void;
  selectedBlobIds: string[];
  onTransferSuccess: () => void;
}

export const DocumentDialogs: React.FC<DocumentDialogsProps> = ({
  collectionTree,

  // Upload dialog props
  uploadDialogOpen,
  uploadDialogTargetId,
  closeUploadDialog,
  handleUploadToCollection,
  companions,

  // Bulk move dialog props
  bulkMoveDialogOpen,
  bulkMoveTargetCollectionId,
  closeBulkMoveDialog,
  executeBulkMove,
  setBulkMoveTargetCollectionId,
  bulkOperationError,
  isProcessing,
  selectedFileCount,

  // Bulk delete dialog props
  bulkDeleteDialogOpen,
  closeBulkDeleteDialog,
  executeBulkDelete,

  // Bulk regenerate dialog props
  bulkRegenerateDialogOpen,
  closeBulkRegenerateDialog,
  executeBulkRegenerate,

  // Transfer to companions dialog props
  transferToCompanionsDialogOpen,
  closeTransferToCompanionsDialog,
  selectedBlobIds,
  onTransferSuccess,
}) => {
  // Helper function to render collection options
  const renderCollectionOptions = (node: CollectionNode, level = 0) => {
    const options = [];
    const prefix = '\u00A0'.repeat(level * 2);

    options.push(
      <option key={node.id} value={node.id}>
        {prefix + (node.name === 'root' ? 'All Files' : node.name)}
      </option>,
    );

    if (node.children) {
      node.children.forEach((child: CollectionNode) => {
        options.push(...renderCollectionOptions(child, level + 1));
      });
    }

    return options;
  };

  return (
    <>
      {/* Upload Dialog */}
      {collectionTree && uploadDialogOpen && uploadDialogTargetId && (
        <UploadDialog
          isOpen={uploadDialogOpen}
          onClose={closeUploadDialog}
          collectionId={uploadDialogTargetId}
          collectionTree={collectionTree}
          onUpload={(files, collectionId, auto, uploadToCompanions, targetCompanionMatterIds) =>
            handleUploadToCollection(
              files,
              collectionId,
              auto,
              uploadToCompanions,
              targetCompanionMatterIds,
            )
          }
          companions={companions}
        />
      )}

      {/* Bulk Move Dialog */}
      <ActionDialog
        isOpen={bulkMoveDialogOpen}
        onClose={closeBulkMoveDialog}
        title="Move Files"
        description={`Move ${selectedFileCount} selected file(s) to a new folder.`}
        confirmButtonText="Move"
        onConfirm={executeBulkMove}
        errorMessage={bulkOperationError}
        isConfirmDisabled={isProcessing}
      >
        <select
          value={bulkMoveTargetCollectionId}
          onChange={(e) => setBulkMoveTargetCollectionId(e.target.value)}
          className="w-full px-3 py-2 border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={isProcessing}
        >
          {collectionTree && renderCollectionOptions(collectionTree)}
        </select>
      </ActionDialog>

      {/* Bulk Delete Dialog */}
      <ActionDialog
        isOpen={bulkDeleteDialogOpen}
        onClose={closeBulkDeleteDialog}
        title="Delete Files"
        description={`Are you sure you want to delete ${selectedFileCount} selected file(s)? This action cannot be undone.`}
        confirmButtonText="Delete"
        onConfirm={executeBulkDelete}
        errorMessage={bulkOperationError}
        isConfirmDisabled={isProcessing}
        isDangerousAction
      />

      {/* Bulk Regenerate Dialog */}
      <ActionDialog
        isOpen={bulkRegenerateDialogOpen}
        onClose={closeBulkRegenerateDialog}
        title="Reprocess Files"
        description={`Are you sure you want to regenerate digests for ${selectedFileCount} selected file(s)?`}
        confirmButtonText="Regenerate"
        onConfirm={executeBulkRegenerate}
        errorMessage={bulkOperationError}
        isConfirmDisabled={isProcessing}
      >
        <div className="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-sm text-amber-800 mb-2">
            This action may cause new coverage/medical provider records to be
            created.
          </p>
          <p className="text-sm text-amber-800">
            It could lead to duplicate Tasks being created. Use with caution!
          </p>
          <p className="text-sm text-amber-800 mt-3">
            This process may take a few minutes to complete.
          </p>
        </div>
      </ActionDialog>

      {/* Transfer to Companions Dialog */}
      {collectionTree?.matter_id && (
        <TransferToCompanionsDialog
          isOpen={transferToCompanionsDialogOpen}
          onClose={closeTransferToCompanionsDialog}
          matterId={collectionTree.matter_id}
          selectedBlobIds={selectedBlobIds}
          onSuccess={onTransferSuccess}
        />
      )}
    </>
  );
};

export default DocumentDialogs;
